// src/features/stock/components/StockMovementForm.js
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  X, 
  Package, 
  ArrowDownCircle, 
  ArrowUpCircle,
  RefreshCw,
  AlertTriangle,
  FileText,
  User,
  Calendar,
  Hash,
  ClipboardList
} from 'lucide-react';
import { format } from 'date-fns';
import Input from '../../../shared/components/Input';
import Button from '../../../shared/components/Button';
import { MOVEMENT_TYPES } from '../constants/stockConstants';

const schema = yup.object({
  type: yup.string()
    .required('Movement type is required')
    .oneOf(['in', 'out', 'adjustment', 'return', 'transfer', 'damaged', 'expired'], 'Invalid movement type'),
  
  quantity: yup.number()
    .required('Quantity is required')
    .positive('Quantity must be positive')
    .typeError('Quantity must be a number'),
  
  unit: yup.string()
    .required('Unit is required'),
  
  reason: yup.string()
    .required('Reason is required')
    .min(3, 'Reason must be at least 3 characters')
    .max(500, 'Reason must be less than 500 characters'),
  
  reference: yup.string()
    .nullable()
    .transform(value => value || null)
    .max(100, 'Reference must be less than 100 characters'),
  
  performed_by: yup.string()
    .nullable()
    .transform(value => value || null)
    .max(100, 'Performed by must be less than 100 characters'),
  
  notes: yup.string()
    .nullable()
    .transform(value => value || null)
    .max(1000, 'Notes must be less than 1000 characters'),
  
  date: yup.date()
    .required('Date is required')
    .max(new Date(), 'Date cannot be in the future'),
  
  from_location: yup.string()
    .when('type', {
      is: (val) => ['out', 'transfer', 'damaged', 'expired'].includes(val),
      then: (schema) => schema.required('Source location is required'),
      otherwise: (schema) => schema.nullable()
    }),
  
  to_location: yup.string()
    .when('type', {
      is: (val) => ['in', 'return', 'transfer'].includes(val),
      then: (schema) => schema.required('Destination location is required'),
      otherwise: (schema) => schema.nullable()
    }),
  
  batch_number: yup.string()
    .nullable(),
  
  expiry_date: yup.date()
    .nullable()
    .when('type', {
      is: 'in',
      then: (schema) => schema.min(new Date(), 'Expiry date must be in the future').nullable()
    }),
  
  recipient: yup.string()
    .when('type', {
      is: 'out',
      then: (schema) => schema.required('Recipient is required'),
      otherwise: (schema) => schema.nullable()
    }),
  
  department: yup.string()
    .when('type', {
      is: 'out',
      then: (schema) => schema.required('Department is required'),
      otherwise: (schema) => schema.nullable()
    }),
  
  patient_id: yup.number()
    .when('type', {
      is: 'out',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable()
    }),
  
  prescription_id: yup.number()
    .when('type', {
      is: 'out',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable()
    }),
  
  supplier: yup.string()
    .when('type', {
      is: 'in',
      then: (schema) => schema.required('Supplier is required'),
      otherwise: (schema) => schema.nullable()
    }),
  
  purchase_order: yup.string()
    .when('type', {
      is: 'in',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable()
    }),
  
  invoice_number: yup.string()
    .when('type', {
      is: 'in',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable()
    }),
  
  cost_per_unit: yup.number()
    .when('type', {
      is: 'in',
      then: (schema) => schema
        .positive('Cost must be positive')
        .nullable()
        .transform(value => value || null),
      otherwise: (schema) => schema.nullable()
    }),
  
  adjustment_type: yup.string()
    .when('type', {
      is: 'adjustment',
      then: (schema) => schema
        .required('Adjustment type is required')
        .oneOf(['positive', 'negative', 'correction'], 'Invalid adjustment type'),
      otherwise: (schema) => schema.nullable()
    }),
  
  damage_reason: yup.string()
    .when('type', {
      is: 'damaged',
      then: (schema) => schema
        .required('Damage reason is required')
        .oneOf(['expired', 'broken', 'contaminated', 'other'], 'Invalid damage reason'),
      otherwise: (schema) => schema.nullable()
    }),
  
  authorized_by: yup.string()
    .when('type', {
      is: (val) => ['adjustment', 'damaged', 'expired'].includes(val),
      then: (schema) => schema.required('Authorization is required'),
      otherwise: (schema) => schema.nullable()
    }),
  
  documents: yup.mixed()
    .nullable(),
});

const StockMovementForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  item,
  initialData = null 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(initialData?.type || 'in');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState(null);

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue, trigger } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: 'in',
      date: format(new Date(), 'yyyy-MM-dd'),
      unit: item?.unit || 'pcs',
      ...initialData
    },
    mode: 'onChange',
  });

  const watchType = watch('type');
  const watchQuantity = watch('quantity');
  const watchAdjustmentType = watch('adjustment_type');

  useEffect(() => {
    if (item) {
      setValue('unit', item.unit);
      setValue('from_location', item.location);
      setValue('batch_number', item.batch_number);
      setValue('expiry_date', item.expiry_date);
    }
  }, [item, setValue]);

  useEffect(() => {
    setSelectedType(watchType);
  }, [watchType]);

  const handleFormSubmit = async (data) => {
    setFormData(data);
    setShowConfirmation(true);
  };

  const confirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        item_id: item?.id,
        item_name: item?.name,
        current_quantity: item?.quantity,
        new_quantity: calculateNewQuantity(item?.quantity, formData),
      });
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to record movement:', error);
    } finally {
      setIsSubmitting(false);
      setShowConfirmation(false);
    }
  };

  const calculateNewQuantity = (currentQty, data) => {
    switch (data.type) {
      case 'in':
      case 'return':
        return currentQty + data.quantity;
      case 'out':
      case 'damaged':
      case 'expired':
        return currentQty - data.quantity;
      case 'adjustment':
        return data.adjustment_type === 'positive' 
          ? currentQty + data.quantity 
          : currentQty - data.quantity;
      default:
        return currentQty;
    }
  };

  const getQuantityWarning = () => {
    if (!item || !watchQuantity) return null;
    
    const newQty = calculateNewQuantity(item.quantity, { 
      type: watchType, 
      quantity: watchQuantity,
      adjustment_type: watchAdjustmentType 
    });
    
    if (newQty < 0) {
      return {
        type: 'error',
        message: 'This movement would result in negative stock!'
      };
    }
    
    if (newQty < (item.reorder_level || 0)) {
      return {
        type: 'warning',
        message: `This will bring stock below reorder level (${item.reorder_level} ${item.unit})`
      };
    }
    
    return null;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'in': return ArrowDownCircle;
      case 'out': return ArrowUpCircle;
      default: return RefreshCw;
    }
  };

  const warning = getQuantityWarning();

  if (!isOpen) return null;

  const TypeIcon = getTypeIcon(selectedType);

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedType === 'in' ? 'bg-green-100' :
                    selectedType === 'out' ? 'bg-red-100' :
                    'bg-yellow-100'
                  }`}>
                    <TypeIcon className={`h-6 w-6 ${
                      selectedType === 'in' ? 'text-green-600' :
                      selectedType === 'out' ? 'text-red-600' :
                      'text-yellow-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Record Stock Movement
                    </h3>
                    {item && (
                      <p className="text-sm text-gray-500">
                        Item: {item.name} (Current: {item.quantity} {item.unit})
                      </p>
                    )}
                  </div>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Warning for low stock */}
              {warning && (
                <div className={`mb-4 p-3 rounded-lg flex items-start gap-3 ${
                  warning.type === 'error' 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <AlertTriangle className={`h-5 w-5 ${
                    warning.type === 'error' ? 'text-red-600' : 'text-yellow-600'
                  } mt-0.5`} />
                  <p className={`text-sm ${
                    warning.type === 'error' ? 'text-red-700' : 'text-yellow-700'
                  }`}>{warning.message}</p>
                </div>
              )}

              <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                {/* Movement Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Movement Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(MOVEMENT_TYPES).map(([key, type]) => {
                      const Icon = getTypeIcon(type.id);
                      return (
                        <label
                          key={type.id}
                          className={`
                            flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-all
                            ${selectedType === type.id
                              ? type.id === 'in' ? 'border-green-500 bg-green-50' :
                                type.id === 'out' ? 'border-red-500 bg-red-50' :
                                'border-yellow-500 bg-yellow-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                            }
                          `}
                        >
                          <input
                            type="radio"
                            {...register('type')}
                            value={type.id}
                            className="sr-only"
                          />
                          <Icon className={`h-5 w-5 mb-1 ${
                            selectedType === type.id
                              ? type.id === 'in' ? 'text-green-600' :
                                type.id === 'out' ? 'text-red-600' :
                                'text-yellow-600'
                              : 'text-gray-400'
                          }`} />
                          <span className={`text-xs font-medium ${
                            selectedType === type.id
                              ? type.id === 'in' ? 'text-green-700' :
                                type.id === 'out' ? 'text-red-700' :
                                'text-yellow-700'
                              : 'text-gray-500'
                          }`}>{type.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                  )}
                </div>

                {/* Basic Movement Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      label="Date"
                      type="date"
                      {...register('date')}
                      error={errors.date?.message}
                      icon={Calendar}
                      required
                    />
                  </div>

                  <div>
                    <Input
                      label="Reference Number"
                      {...register('reference')}
                      error={errors.reference?.message}
                      icon={Hash}
                      placeholder="PO-12345, etc."
                    />
                  </div>

                  <div>
                    <Input
                      label="Quantity"
                      type="number"
                      step="0.01"
                      {...register('quantity')}
                      error={errors.quantity?.message}
                      placeholder="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit
                    </label>
                    <input
                      type="text"
                      {...register('unit')}
                      readOnly
                      className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Stock In Specific Fields */}
                {selectedType === 'in' && (
                  <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-800">Stock In Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Input
                          label="Supplier"
                          {...register('supplier')}
                          error={errors.supplier?.message}
                          placeholder="Supplier name"
                          required
                        />
                      </div>
                      <Input
                        label="Purchase Order"
                        {...register('purchase_order')}
                        placeholder="PO number"
                      />
                      <Input
                        label="Invoice Number"
                        {...register('invoice_number')}
                        placeholder="Invoice number"
                      />
                      <Input
                        label="Cost per Unit ($)"
                        type="number"
                        step="0.01"
                        {...register('cost_per_unit')}
                        error={errors.cost_per_unit?.message}
                        placeholder="0.00"
                      />
                      <Input
                        label="Batch Number"
                        {...register('batch_number')}
                        placeholder="Batch number"
                      />
                      <Input
                        label="Expiry Date"
                        type="date"
                        {...register('expiry_date')}
                        error={errors.expiry_date?.message}
                      />
                      <div className="col-span-2">
                        <Input
                          label="To Location"
                          {...register('to_location')}
                          error={errors.to_location?.message}
                          placeholder="Destination location"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Stock Out Specific Fields */}
                {selectedType === 'out' && (
                  <div className="space-y-4 p-4 bg-red-50 rounded-lg">
                    <h4 className="font-medium text-red-800">Stock Out Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Input
                          label="Recipient"
                          {...register('recipient')}
                          error={errors.recipient?.message}
                          icon={User}
                          placeholder="Recipient name"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          label="Department"
                          {...register('department')}
                          error={errors.department?.message}
                          placeholder="Department"
                          required
                        />
                      </div>
                      <Input
                        label="Patient ID (Optional)"
                        type="number"
                        {...register('patient_id')}
                        placeholder="Patient ID"
                      />
                      <Input
                        label="Prescription ID (Optional)"
                        type="number"
                        {...register('prescription_id')}
                        placeholder="Prescription ID"
                      />
                      <div className="col-span-2">
                        <Input
                          label="From Location"
                          {...register('from_location')}
                          error={errors.from_location?.message}
                          placeholder="Source location"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Adjustment Specific Fields */}
                {selectedType === 'adjustment' && (
                  <div className="space-y-4 p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-medium text-yellow-800">Adjustment Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Adjustment Type <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              {...register('adjustment_type')}
                              value="positive"
                              className="mr-2"
                            />
                            <span>Increase (+)</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              {...register('adjustment_type')}
                              value="negative"
                              className="mr-2"
                            />
                            <span>Decrease (-)</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              {...register('adjustment_type')}
                              value="correction"
                              className="mr-2"
                            />
                            <span>Correction</span>
                          </label>
                        </div>
                        {errors.adjustment_type && (
                          <p className="mt-1 text-sm text-red-600">{errors.adjustment_type.message}</p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <Input
                          label="Authorized By"
                          {...register('authorized_by')}
                          error={errors.authorized_by?.message}
                          placeholder="Authorizing person"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Damaged/Expired Specific Fields */}
                {['damaged', 'expired'].includes(selectedType) && (
                  <div className="space-y-4 p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-medium text-orange-800">
                      {selectedType === 'damaged' ? 'Damage Details' : 'Expiry Details'}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedType === 'damaged' && (
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Damage Reason <span className="text-red-500">*</span>
                          </label>
                          <select
                            {...register('damage_reason')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">Select reason</option>
                            <option value="expired">Expired</option>
                            <option value="broken">Broken/Damaged</option>
                            <option value="contaminated">Contaminated</option>
                            <option value="other">Other</option>
                          </select>
                          {errors.damage_reason && (
                            <p className="mt-1 text-sm text-red-600">{errors.damage_reason.message}</p>
                          )}
                        </div>
                      )}
                      <div className="col-span-2">
                        <Input
                          label="Authorized By"
                          {...register('authorized_by')}
                          error={errors.authorized_by?.message}
                          placeholder="Authorizing person"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          label="From Location"
                          {...register('from_location')}
                          error={errors.from_location?.message}
                          placeholder="Source location"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Return Specific Fields */}
                {selectedType === 'return' && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-800">Return Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Input
                          label="From Location"
                          {...register('from_location')}
                          error={errors.from_location?.message}
                          placeholder="Return from location"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          label="To Location"
                          {...register('to_location')}
                          error={errors.to_location?.message}
                          placeholder="Return to location"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Transfer Specific Fields */}
                {selectedType === 'transfer' && (
                  <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-800">Transfer Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Input
                          label="From Location"
                          {...register('from_location')}
                          error={errors.from_location?.message}
                          placeholder="Source location"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          label="To Location"
                          {...register('to_location')}
                          error={errors.to_location?.message}
                          placeholder="Destination location"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Common Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    {...register('reason')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Reason for this movement"
                  />
                  {errors.reason && (
                    <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Performed By
                  </label>
                  <Input
                    {...register('performed_by')}
                    icon={User}
                    placeholder="Person performing the movement"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    {...register('notes')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Any additional notes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supporting Documents
                  </label>
                  <input
                    type="file"
                    {...register('documents')}
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Upload receipts, prescriptions, or other supporting documents
                  </p>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                  >
                    Record Movement
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ClipboardList className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Confirm Stock Movement
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Please review the movement details before confirming:
                      </p>
                      <div className="mt-4 space-y-2 text-sm">
                        <p><span className="font-medium">Item:</span> {item?.name}</p>
                        <p><span className="font-medium">Type:</span> {MOVEMENT_TYPES[formData?.type?.toUpperCase()]?.name}</p>
                        <p><span className="font-medium">Quantity:</span> {formData?.quantity} {formData?.unit}</p>
                        <p><span className="font-medium">Current Stock:</span> {item?.quantity} {item?.unit}</p>
                        <p><span className="font-medium">New Stock:</span> {calculateNewQuantity(item?.quantity, formData)} {item?.unit}</p>
                        <p><span className="font-medium">Reason:</span> {formData?.reason}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  type="button"
                  variant="primary"
                  onClick={confirmSubmit}
                  isLoading={isSubmitting}
                  className="w-full sm:w-auto sm:ml-3"
                >
                  Confirm
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowConfirmation(false)}
                  className="mt-3 w-full sm:w-auto sm:mt-0"
                >
                  Review
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StockMovementForm;